import { useEffect, useMemo, useRef, useState } from "react";
import {
  Sparkle, MapPin, Camera, CheckCircle, CurrencyInr, Clock, ChartLineUp,
  Users, ChatCircleText, TreeStructure, DeviceMobile, Buildings, Star,
  Play, Pause, ArrowRight, GraduationCap, Briefcase,
} from "@phosphor-icons/react";
import { Link } from "react-router-dom";

/**
 * Zenith360 HR — 90-second auto-looping landing teaser.
 * 16 scenes at ~5.6s each. Renders inside a browser-chrome frame,
 * with a progress bar + click-to-jump ticker.  All animation is CSS/SVG.
 */

// ---------- Scene primitives ---------------------------------------------
function Stat({ label, value, sub, delay = 0 }) {
  return (
    <div
      className="p-3 bg-white/10 backdrop-blur-sm border border-white/15 rounded-sm text-white opacity-0"
      style={{ animation: `fadeUp 0.6s ease-out ${delay}s forwards` }}
    >
      <div className="text-[10px] uppercase tracking-widest text-zenith-green/90 font-bold">{label}</div>
      <div className="font-display text-2xl font-bold mt-1">{value}</div>
      {sub && <div className="text-[10px] text-white/70 mt-0.5">{sub}</div>}
    </div>
  );
}

function AnimatedBar({ width, label, delay, tone = "green" }) {
  const colors = { green: "#33cc33", teal: "#12b3a8", magenta: "#e6007a", navy: "#1a2d59" };
  return (
    <div className="opacity-0" style={{ animation: `fadeUp 0.5s ease-out ${delay}s forwards` }}>
      <div className="text-[10px] uppercase tracking-widest text-white/70 mb-1 font-semibold">{label}</div>
      <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
        <div className="h-full rounded-full" style={{ background: colors[tone], animation: `growBar 1.4s ease-out ${delay + 0.2}s forwards`, width: 0, "--w": width }} />
      </div>
    </div>
  );
}

function KanbanCol({ title, cards, delay }) {
  return (
    <div className="flex-1 min-w-0 opacity-0" style={{ animation: `fadeUp 0.5s ease-out ${delay}s forwards` }}>
      <div className="text-[10px] uppercase tracking-widest text-white/80 font-bold mb-2">{title}</div>
      <div className="space-y-2">
        {cards.map((c, i) => (
          <div key={i} className="p-2 bg-white/90 rounded-sm text-[11px] text-adb-navy shadow-sm opacity-0"
               style={{ animation: `slideIn 0.5s ease-out ${delay + 0.15 * (i + 1)}s forwards` }}>
            {c}
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------- Scenes -------------------------------------------------------
function LogoScene() {
  return (
    <div className="absolute inset-0 grid place-items-center">
      <div className="text-center">
        <div className="relative inline-block">
          <div className="absolute -inset-4 bg-gradient-to-br from-zenith-teal/40 via-zenith-magenta/40 to-zenith-green/40 rounded-2xl blur-2xl animate-pulse" />
          <div className="relative w-28 h-28 bg-white rounded-xl p-3 grid place-items-center border border-white/20 shadow-2xl">
            <img src="/zenith360-logo.png" alt="Zenith360" className="max-w-full max-h-full object-contain" />
          </div>
        </div>
        <div className="font-display text-5xl font-bold text-white tracking-tight mt-6 opacity-0" style={{ animation: "fadeUp 0.8s ease-out 0.4s forwards" }}>
          Zenith<span className="text-zenith-green">360</span> HR
        </div>
        <div className="text-[13px] uppercase tracking-[0.32em] text-zenith-green mt-3 font-bold opacity-0" style={{ animation: "fadeUp 0.8s ease-out 0.9s forwards" }}>
          Your complete HR Ecosystem
        </div>
      </div>
    </div>
  );
}

function ThreeModesScene() {
  const modes = [
    { title: "Organization", subtitle: "Single company", icon: Buildings, color: "#1a2d59" },
    { title: "Group of Companies", subtitle: "Sister entities", icon: TreeStructure, color: "#12b3a8" },
    { title: "HR Agency", subtitle: "Managed clients", icon: Users, color: "#e6007a" },
  ];
  return (
    <div className="absolute inset-0 grid place-items-center px-8">
      <div>
        <div className="text-center mb-6 opacity-0" style={{ animation: "fadeUp 0.5s ease-out 0s forwards" }}>
          <div className="text-[11px] uppercase tracking-[0.3em] text-zenith-green font-bold">Sign up in seconds</div>
          <div className="font-display text-3xl font-bold text-white mt-2">Three ways in. One product.</div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          {modes.map((m, i) => {
            const Icon = m.icon;
            return (
              <div key={m.title} className="p-4 bg-white/95 rounded-md border border-white/20 opacity-0 text-adb-navy min-w-[150px]"
                   style={{ animation: `slideIn 0.6s ease-out ${0.3 + i * 0.25}s forwards` }}>
                <div className="w-8 h-8 rounded-full grid place-items-center" style={{ background: `${m.color}20`, color: m.color }}>
                  <Icon size={18} weight="fill" />
                </div>
                <div className="font-display font-bold text-sm mt-3">{m.title}</div>
                <div className="text-[10px] text-zinc-500 mt-1">{m.subtitle}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function DashboardScene() {
  return (
    <div className="absolute inset-0 p-6">
      <div className="text-[11px] uppercase tracking-widest text-zenith-green font-bold opacity-0" style={{ animation: "fadeUp 0.5s ease-out 0s forwards" }}>Live dashboard</div>
      <div className="font-display text-2xl font-bold text-white opacity-0" style={{ animation: "fadeUp 0.5s ease-out 0.15s forwards" }}>Everything at a glance.</div>
      <div className="grid grid-cols-4 gap-3 mt-4">
        <Stat label="Headcount" value="412" sub="+18 MTD" delay={0.4} />
        <Stat label="Present today" value="94%" sub="+2.1 pts" delay={0.55} />
        <Stat label="Payroll ready" value="₹28.4L" sub="on-time" delay={0.7} />
        <Stat label="eNPS" value="62" sub="↑ vs Q2" delay={0.85} />
      </div>
      <svg viewBox="0 0 600 90" className="w-full h-20 mt-6" fill="none">
        <defs><linearGradient id="dashFill" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#33cc33" stopOpacity="0.4" /><stop offset="100%" stopColor="#33cc33" stopOpacity="0" /></linearGradient></defs>
        <path d="M0,70 C60,55 120,60 180,45 C240,30 300,42 360,32 C420,20 480,38 540,18 L600,15 L600,90 L0,90 Z" fill="url(#dashFill)" opacity="0" style={{ animation: "fadeUp 0.8s ease-out 1.1s forwards" }} />
        <path d="M0,70 C60,55 120,60 180,45 C240,30 300,42 360,32 C420,20 480,38 540,18 L600,15"
              stroke="#33cc33" strokeWidth="2" fill="none" strokeDasharray="1000" strokeDashoffset="1000"
              style={{ animation: "drawLine 2s ease-out 1s forwards" }} />
      </svg>
    </div>
  );
}

function CheckInScene() {
  return (
    <div className="absolute inset-0 grid place-items-center">
      <div className="flex items-center gap-8">
        {/* Map with animated pin */}
        <div className="relative w-56 h-56 rounded-md bg-gradient-to-br from-zenith-teal/40 to-zenith-magenta/30 border border-white/20 overflow-hidden opacity-0" style={{ animation: "fadeUp 0.5s ease-out 0s forwards" }}>
          <div className="absolute inset-0 opacity-40" style={{ backgroundImage: "radial-gradient(circle at 30% 40%, rgba(255,255,255,0.3) 0%, transparent 40%), radial-gradient(circle at 70% 60%, rgba(255,255,255,0.2) 0%, transparent 30%)" }} />
          {/* grid lines */}
          {[...Array(6)].map((_, i) => <div key={`h${i}`} className="absolute left-0 right-0 border-t border-white/10" style={{ top: `${(i + 1) * 14}%` }} />)}
          {[...Array(6)].map((_, i) => <div key={`v${i}`} className="absolute top-0 bottom-0 border-l border-white/10" style={{ left: `${(i + 1) * 14}%` }} />)}
          {/* Pin */}
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-full" style={{ animation: "dropPin 0.9s cubic-bezier(0.68,-0.55,0.27,1.55) 0.5s both" }}>
            <MapPin size={44} weight="fill" className="text-zenith-magenta drop-shadow-lg" />
          </div>
          {/* Radius */}
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-zenith-green/60"
               style={{ width: 0, height: 0, animation: "pulseRing 1.2s ease-out 1.2s forwards" }} />
        </div>
        <div className="space-y-3">
          <div className="text-[11px] uppercase tracking-widest text-zenith-green font-bold opacity-0" style={{ animation: "fadeUp 0.4s 0s forwards" }}>Attendance</div>
          <div className="font-display text-2xl font-bold text-white leading-tight opacity-0 max-w-xs" style={{ animation: "fadeUp 0.4s 0.15s forwards" }}>
            Geo-fenced check-in with selfie
          </div>
          <div className="flex items-center gap-2 opacity-0" style={{ animation: "fadeUp 0.4s 1.6s forwards" }}>
            <div className="w-8 h-8 rounded-sm bg-adb-navy border-2 border-zenith-green grid place-items-center">
              <Camera size={14} className="text-zenith-green" weight="fill" />
            </div>
            <div className="w-8 h-8 rounded-full bg-zenith-green grid place-items-center" style={{ animation: "popIn 0.4s 2.1s forwards", transform: "scale(0)" }}>
              <CheckCircle size={16} className="text-adb-navy" weight="fill" />
            </div>
            <span className="text-xs text-white font-semibold ml-1 opacity-0" style={{ animation: "fadeUp 0.3s 2.4s forwards" }}>Punched in · 09:04</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function LeavesScene() {
  return (
    <div className="absolute inset-0 p-6">
      <div className="text-[11px] uppercase tracking-widest text-zenith-green font-bold opacity-0" style={{ animation: "fadeUp 0.4s 0s forwards" }}>Leaves & Approvals</div>
      <div className="font-display text-2xl font-bold text-white opacity-0" style={{ animation: "fadeUp 0.4s 0.15s forwards" }}>Multi-level. Frictionless.</div>
      <div className="grid grid-cols-4 gap-2 mt-6">
        {[
          { l: "Applied", cards: ["Priya · CL · 2d"] },
          { l: "Manager", cards: ["Approved ✓"] },
          { l: "HR", cards: ["Approved ✓"] },
          { l: "Payroll", cards: ["Synced ✓"] },
        ].map((c, i) => <KanbanCol key={c.l} title={c.l} cards={c.cards} delay={0.4 + i * 0.35} />)}
      </div>
    </div>
  );
}

function PayrollScene() {
  return (
    <div className="absolute inset-0 p-6">
      <div className="text-[11px] uppercase tracking-widest text-zenith-green font-bold opacity-0" style={{ animation: "fadeUp 0.4s 0s forwards" }}>Payroll</div>
      <div className="font-display text-2xl font-bold text-white opacity-0" style={{ animation: "fadeUp 0.4s 0.15s forwards" }}>Statutory-compliant, one click.</div>
      <div className="mt-5 space-y-2.5 max-w-xl">
        {[
          { l: "Basic pay", w: "68%", d: 0.4, tone: "teal" },
          { l: "HRA · Special", w: "22%", d: 0.6, tone: "green" },
          { l: "PF (12%) · ESIC · TDS", w: "18%", d: 0.85, tone: "magenta" },
          { l: "Net salary", w: "82%", d: 1.15, tone: "navy" },
        ].map((r) => <AnimatedBar key={r.l} label={r.l} width={r.w} delay={r.d} tone={r.tone} />)}
      </div>
      <div className="mt-6 inline-flex items-center gap-3 px-4 py-2.5 bg-zenith-green rounded-sm text-adb-navy font-bold opacity-0" style={{ animation: "fadeUp 0.5s 1.7s forwards" }}>
        <CurrencyInr size={18} weight="bold" /> ₹28,42,600 · disbursed on time
      </div>
    </div>
  );
}

function TimesheetScene() {
  const rows = ["Mon", "Tue", "Wed", "Thu", "Fri"];
  const bars = [8, 9, 8.5, 9.5, 7];
  return (
    <div className="absolute inset-0 p-6">
      <div className="text-[11px] uppercase tracking-widest text-zenith-green font-bold opacity-0" style={{ animation: "fadeUp 0.4s 0s forwards" }}>Timesheets</div>
      <div className="font-display text-2xl font-bold text-white opacity-0" style={{ animation: "fadeUp 0.4s 0.15s forwards" }}>Weekly billable + project split.</div>
      <div className="mt-6 space-y-2 max-w-2xl">
        {rows.map((r, i) => (
          <div key={r} className="flex items-center gap-3 opacity-0" style={{ animation: `fadeUp 0.4s ${0.4 + i * 0.15}s forwards` }}>
            <div className="w-10 text-[11px] uppercase tracking-widest text-white/70 font-semibold">{r}</div>
            <div className="flex-1 h-6 bg-white/10 rounded-sm relative overflow-hidden">
              <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-zenith-teal to-zenith-green"
                   style={{ width: 0, animation: `growBar 0.8s ease-out ${0.6 + i * 0.15}s forwards`, "--w": `${(bars[i] / 10) * 100}%` }} />
            </div>
            <div className="w-14 text-right text-xs font-mono-plex text-white opacity-0" style={{ animation: `fadeUp 0.3s ${1.4 + i * 0.05}s forwards` }}>{bars[i]}h</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function NineBoxScene() {
  const dots = [
    { x: 15, y: 80, delay: 0.6 }, { x: 45, y: 70, delay: 0.75 },
    { x: 70, y: 45, delay: 0.9 }, { x: 30, y: 40, delay: 1.05 },
    { x: 85, y: 20, delay: 1.2 }, { x: 60, y: 15, delay: 1.35 },
    { x: 20, y: 20, delay: 1.5 },
  ];
  return (
    <div className="absolute inset-0 p-6 grid grid-cols-2 gap-6 items-center">
      <div>
        <div className="text-[11px] uppercase tracking-widest text-zenith-green font-bold opacity-0" style={{ animation: "fadeUp 0.4s 0s forwards" }}>Performance</div>
        <div className="font-display text-2xl font-bold text-white leading-tight opacity-0" style={{ animation: "fadeUp 0.4s 0.15s forwards" }}>KRA weights.<br /> 9-box grid. Auto-cycles.</div>
        <div className="text-sm text-white/70 mt-3 max-w-sm opacity-0" style={{ animation: "fadeUp 0.4s 0.35s forwards" }}>
          Set weighted KRAs, weight-rolled KPIs and see every employee on the 9-box in real time.
        </div>
      </div>
      <div className="relative aspect-square max-w-[240px] mx-auto opacity-0" style={{ animation: "fadeUp 0.5s 0.4s forwards" }}>
        <div className="absolute inset-0 grid grid-cols-3 grid-rows-3 gap-1">
          {[...Array(9)].map((_, i) => {
            const perf = ["low", "med", "high"];
            const potBg = ["bg-white/5", "bg-zenith-teal/10", "bg-zenith-green/15"];
            return <div key={i} className={`border border-white/10 rounded-sm ${potBg[Math.floor(i / 3)]}`} />;
          })}
        </div>
        {dots.map((d, i) => (
          <div key={i} className="absolute w-3 h-3 rounded-full bg-zenith-magenta shadow-lg"
               style={{ left: `${d.x}%`, top: `${d.y}%`, opacity: 0, animation: `popIn 0.4s ${d.delay}s forwards`, transform: "translate(-50%,-50%) scale(0)" }} />
        ))}
        <div className="absolute -bottom-6 left-0 text-[9px] uppercase tracking-widest text-white/60">performance →</div>
        <div className="absolute top-0 -left-6 rotate-[-90deg] origin-top-left text-[9px] uppercase tracking-widest text-white/60">potential →</div>
      </div>
    </div>
  );
}

function ReviewerPickerScene() {
  const chips = [
    { label: "+ Dotted-line", tone: "border-zenith-magenta/60 bg-zenith-magenta text-white", delay: 0.4 },
    { label: "+ Solid-line", tone: "border-adb-navy/60 bg-white text-adb-navy", delay: 0.7 },
    { label: "+ Direct reports", tone: "border-zenith-teal/60 bg-white text-zenith-teal-700", delay: 1.0 },
    { label: "+ Peers", tone: "border-zinc-300 bg-white text-zinc-700", delay: 1.3 },
  ];
  return (
    <div className="absolute inset-0 p-6">
      <div className="text-[11px] uppercase tracking-widest text-zenith-green font-bold opacity-0" style={{ animation: "fadeUp 0.4s 0s forwards" }}>Smart 360° reviewer picker</div>
      <div className="font-display text-2xl font-bold text-white opacity-0" style={{ animation: "fadeUp 0.4s 0.15s forwards" }}>One click. Right reviewers.</div>
      <div className="mt-6 flex flex-wrap gap-2">
        {chips.map((c) => (
          <div key={c.label} className={`px-3 py-1.5 rounded-sm text-xs font-semibold border ${c.tone} opacity-0`}
               style={{ animation: `popIn 0.4s ${c.delay}s forwards`, transform: "scale(0.8)" }}>
            {c.label}
          </div>
        ))}
      </div>
      <div className="mt-5 space-y-1.5 max-w-lg">
        {[
          { n: "Priya Nair", r: "reporting-manager", tone: "border-adb-navy/40 bg-adb-navy/10 text-white", delay: 1.7 },
          { n: "Rahul Verma", r: "dotted-manager", tone: "border-zenith-magenta/60 bg-zenith-magenta/20 text-white", delay: 1.9 },
          { n: "Anaya Krishnan", r: "direct-report", tone: "border-zenith-teal/60 bg-zenith-teal/20 text-white", delay: 2.1 },
        ].map((p) => (
          <div key={p.n} className="flex items-center gap-2 opacity-0" style={{ animation: `slideIn 0.4s ${p.delay}s forwards` }}>
            <input type="checkbox" checked readOnly className="accent-adb-navy" />
            <span className="text-sm text-white flex-1">{p.n}</span>
            <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm border ${p.tone}`}>{p.r}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function LMSScene() {
  const courses = [
    { t: "POSH Compliance", p: 100, cert: true, delay: 0.4 },
    { t: "AI for Managers", p: 78, delay: 0.55 },
    { t: "Data Privacy", p: 45, delay: 0.7 },
    { t: "Leadership Basics", p: 90, cert: true, delay: 0.85 },
  ];
  return (
    <div className="absolute inset-0 p-6">
      <div className="text-[11px] uppercase tracking-widest text-zenith-green font-bold opacity-0" style={{ animation: "fadeUp 0.4s 0s forwards" }}>Learning management</div>
      <div className="font-display text-2xl font-bold text-white opacity-0" style={{ animation: "fadeUp 0.4s 0.15s forwards" }}>Courses. Progress. Certificates.</div>
      <div className="grid grid-cols-2 gap-3 mt-5">
        {courses.map((c) => (
          <div key={c.t} className="bg-white/10 border border-white/15 rounded-sm p-3 opacity-0" style={{ animation: `fadeUp 0.5s ${c.delay}s forwards` }}>
            <div className="flex items-center gap-2 mb-2">
              <GraduationCap size={16} className="text-zenith-green" weight="duotone" />
              <div className="text-sm text-white font-semibold flex-1 truncate">{c.t}</div>
              {c.cert && (
                <div className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 bg-zenith-green text-adb-navy rounded-sm opacity-0" style={{ animation: `popIn 0.4s ${c.delay + 1.4}s forwards`, transform: "scale(0.6)" }}>
                  Certified
                </div>
              )}
            </div>
            <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
              <div className="h-full bg-zenith-teal rounded-full" style={{ width: 0, animation: `growBar 1.2s ease-out ${c.delay + 0.3}s forwards`, "--w": `${c.p}%` }} />
            </div>
            <div className="text-[10px] text-white/60 mt-1 font-mono-plex">{c.p}%</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ATSScene() {
  const stages = [
    { t: "Sourced", cards: ["Aditi K"] },
    { t: "Screened", cards: ["Aditi K"] },
    { t: "Interview", cards: ["Aditi K"] },
    { t: "Offer", cards: ["Aditi K"] },
    { t: "Hired ✓", cards: ["Aditi K"] },
  ];
  return (
    <div className="absolute inset-0 p-6">
      <div className="text-[11px] uppercase tracking-widest text-zenith-green font-bold opacity-0" style={{ animation: "fadeUp 0.4s 0s forwards" }}>Applicant tracking</div>
      <div className="font-display text-2xl font-bold text-white opacity-0" style={{ animation: "fadeUp 0.4s 0.15s forwards" }}>Sourced → Hired in one board.</div>
      <div className="grid grid-cols-5 gap-2 mt-6">
        {stages.map((s, i) => (
          <div key={s.t} className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-white/80 font-bold mb-2 truncate">{s.t}</div>
            <div className="h-14 border border-dashed border-white/20 rounded-sm relative">
              <div className="absolute inset-1 bg-white/95 rounded-sm p-1.5 shadow flex items-center gap-1 text-[11px] text-adb-navy opacity-0"
                   style={{ animation: `atsMove 5s cubic-bezier(0.5,0,0.5,1) ${0.5}s forwards`, animationDelay: `${0.5 + i * 0.9}s`, opacity: 0 }}>
                <Briefcase size={12} weight="fill" className="text-zenith-magenta" /> Aditi K
              </div>
            </div>
          </div>
        ))}
      </div>
      <style>{`@keyframes atsMove { 0% { opacity: 1; } 90% { opacity: 1; } 100% { opacity: 0; } }`}</style>
    </div>
  );
}

function ChatAIScene() {
  const messages = [
    { role: "user", text: "How many casual leaves do I have left?", delay: 0.5 },
    { role: "ai", text: "You have 5 CL and 3 SL remaining this year. Apply here: /app/leaves", delay: 1.8 },
  ];
  return (
    <div className="absolute inset-0 grid place-items-center p-6">
      <div className="w-full max-w-md bg-white rounded-md border border-zinc-200 shadow-2xl overflow-hidden opacity-0" style={{ animation: "fadeUp 0.5s 0s forwards" }}>
        <div className="bg-gradient-to-r from-zenith-blue to-adb-navy p-3 text-white flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-zenith-teal via-zenith-green to-zenith-magenta grid place-items-center">
            <Sparkle size={14} weight="fill" className="text-white" />
          </div>
          <div>
            <div className="font-display font-bold text-sm">Ask Zenith360</div>
            <div className="text-[10px] text-white/60">Claude Sonnet 4.5 · HR-tuned</div>
          </div>
        </div>
        <div className="p-4 space-y-2 min-h-[130px]">
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"} opacity-0`} style={{ animation: `fadeUp 0.4s ${m.delay}s forwards` }}>
              <div className={`max-w-[80%] text-[12px] px-2.5 py-1.5 rounded-sm ${m.role === "user" ? "bg-adb-navy text-white" : "bg-zinc-100 text-adb-navy"}`}>
                {m.text}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function OrgChartScene() {
  return (
    <div className="absolute inset-0 p-6 grid grid-cols-2 gap-6 items-center">
      <div>
        <div className="text-[11px] uppercase tracking-widest text-zenith-green font-bold opacity-0" style={{ animation: "fadeUp 0.4s 0s forwards" }}>Cross-tenant org chart</div>
        <div className="font-display text-2xl font-bold text-white leading-tight opacity-0" style={{ animation: "fadeUp 0.4s 0.15s forwards" }}>Matrix reporting.<br /> Across companies.</div>
        <div className="text-sm text-white/70 mt-3 opacity-0" style={{ animation: "fadeUp 0.4s 0.3s forwards" }}>
          <span className="inline-flex items-center gap-1 mr-3"><span className="w-6 border-t border-white/70"></span> solid</span>
          <span className="inline-flex items-center gap-1"><span className="w-6 border-t border-dashed border-zenith-magenta"></span> dotted</span>
        </div>
      </div>
      <svg viewBox="0 0 300 200" className="w-full max-w-md">
        {/* Solid lines */}
        <path d="M150 30 L90 90" stroke="rgba(255,255,255,0.6)" strokeWidth="2" fill="none" strokeDasharray="80" strokeDashoffset="80" style={{ animation: "drawLine 0.6s ease-out 0.5s forwards" }} />
        <path d="M150 30 L210 90" stroke="rgba(255,255,255,0.6)" strokeWidth="2" fill="none" strokeDasharray="80" strokeDashoffset="80" style={{ animation: "drawLine 0.6s ease-out 0.7s forwards" }} />
        <path d="M90 90 L60 160" stroke="rgba(255,255,255,0.6)" strokeWidth="2" fill="none" strokeDasharray="80" strokeDashoffset="80" style={{ animation: "drawLine 0.6s ease-out 0.9s forwards" }} />
        <path d="M210 90 L240 160" stroke="rgba(255,255,255,0.6)" strokeWidth="2" fill="none" strokeDasharray="80" strokeDashoffset="80" style={{ animation: "drawLine 0.6s ease-out 1.1s forwards" }} />
        {/* Dotted cross-tenant line */}
        <path d="M60 160 L240 160" stroke="#e6007a" strokeWidth="2" fill="none" strokeDasharray="6 4" strokeDashoffset="180" style={{ animation: "drawLine 0.8s ease-out 1.6s forwards" }} />
        {/* Nodes */}
        {[
          { x: 150, y: 30, l: "CEO", t: "Group HQ", d: 0.3 },
          { x: 90, y: 90, l: "MD A", t: "Company A", d: 0.9 },
          { x: 210, y: 90, l: "MD B", t: "Company B", d: 1.0 },
          { x: 60, y: 160, l: "Head A", t: "Company A", d: 1.3 },
          { x: 240, y: 160, l: "Head B", t: "Company B", d: 1.5 },
        ].map((n) => (
          <g key={n.l} opacity="0" style={{ animation: `popIn 0.4s ${n.d}s forwards` }}>
            <circle cx={n.x} cy={n.y} r="12" fill="#ffffff" />
            <text x={n.x} y={n.y + 3} fontSize="8" fontWeight="700" textAnchor="middle" fill="#1a2d59">{n.l}</text>
            <text x={n.x} y={n.y + 24} fontSize="7" textAnchor="middle" fill="rgba(255,255,255,0.6)">{n.t}</text>
          </g>
        ))}
      </svg>
    </div>
  );
}

function BillingScene() {
  const rows = [
    { name: "Group HQ", share: 24, sub: 4560, delay: 0.6 },
    { name: "Meridian Labs", share: 42, sub: 7980, delay: 0.9 },
    { name: "Vertex Retail", share: 18, sub: 3420, delay: 1.2 },
    { name: "Kinetic Cloud", share: 16, sub: 3040, delay: 1.5 },
  ];
  return (
    <div className="absolute inset-0 p-6">
      <div className="text-[11px] uppercase tracking-widest text-zenith-green font-bold opacity-0" style={{ animation: "fadeUp 0.4s 0s forwards" }}>Consolidated group billing</div>
      <div className="font-display text-2xl font-bold text-white opacity-0" style={{ animation: "fadeUp 0.4s 0.15s forwards" }}>See who's driving the bill.</div>
      <div className="mt-5 space-y-2 max-w-2xl">
        {rows.map((r) => (
          <div key={r.name} className="flex items-center gap-3 text-white opacity-0" style={{ animation: `fadeUp 0.5s ${r.delay}s forwards` }}>
            <div className="w-32 text-xs truncate">{r.name}</div>
            <div className="flex-1 h-2 bg-white/10 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-zenith-teal to-zenith-magenta" style={{ width: 0, animation: `growBar 1s ease-out ${r.delay + 0.2}s forwards`, "--w": `${r.share}%` }} />
            </div>
            <div className="w-8 text-right text-xs font-mono-plex">{r.share}%</div>
            <div className="w-20 text-right text-xs font-mono-plex text-zenith-green">₹{r.sub.toLocaleString("en-IN")}</div>
          </div>
        ))}
        <div className="flex items-center gap-3 pt-2 border-t border-white/10 text-white opacity-0" style={{ animation: "fadeUp 0.5s 1.9s forwards" }}>
          <div className="w-32 text-sm font-bold">Total</div>
          <div className="flex-1" />
          <div className="w-8 text-right text-xs font-mono-plex">100%</div>
          <div className="w-20 text-right text-sm font-bold text-zenith-green font-mono-plex">₹19,000</div>
        </div>
      </div>
    </div>
  );
}

function MobileScene() {
  return (
    <div className="absolute inset-0 grid place-items-center">
      <div className="flex items-end gap-4">
        {["Android", "iOS", "PWA"].map((label, i) => (
          <div key={label} className="opacity-0" style={{ animation: `fadeUp 0.6s ease-out ${0.3 + i * 0.25}s forwards` }}>
            <div className="w-24 h-44 md:w-28 md:h-52 rounded-2xl border-4 border-white/60 bg-gradient-to-b from-adb-navy to-zenith-blue p-1 shadow-2xl relative">
              <div className="w-full h-full rounded-xl bg-adb-navy border border-white/10 grid place-items-center overflow-hidden">
                <div className="text-center px-2">
                  <div className="w-8 h-8 mx-auto bg-white rounded-sm p-0.5 grid place-items-center">
                    <img src="/zenith360-logo.png" alt="" className="max-w-full max-h-full object-contain" />
                  </div>
                  <div className="text-[9px] font-display font-bold text-white mt-2">Zenith<span className="text-zenith-green">360</span></div>
                  <DeviceMobile size={11} className="text-zenith-green mx-auto mt-2" weight="fill" />
                </div>
              </div>
              <div className="absolute top-1 left-1/2 -translate-x-1/2 w-8 h-1 rounded-full bg-white/40" />
            </div>
            <div className="text-center text-[11px] uppercase tracking-widest text-white/80 font-semibold mt-3">{label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function OutroScene() {
  return (
    <div className="absolute inset-0 grid place-items-center">
      <div className="text-center">
        <div className="text-[11px] uppercase tracking-[0.3em] text-zenith-green font-bold opacity-0" style={{ animation: "fadeUp 0.5s 0s forwards" }}>Start free · 14-day trial</div>
        <div className="font-display text-4xl md:text-5xl font-bold text-white mt-3 leading-tight opacity-0" style={{ animation: "fadeUp 0.7s 0.3s forwards" }}>
          One HR platform. <br /><span className="font-serif-display italic bg-gradient-to-r from-zenith-teal via-zenith-green to-zenith-magenta bg-clip-text text-transparent">Every workspace.</span>
        </div>
        <div className="mt-6 flex items-center justify-center gap-3 opacity-0" style={{ animation: "fadeUp 0.5s 1.0s forwards" }}>
          <Link to="/signup" className="inline-flex items-center gap-2 px-5 py-2.5 bg-zenith-green hover:brightness-110 text-adb-navy rounded-sm font-bold text-sm">
            Start free <ArrowRight size={14} />
          </Link>
          <Link to="/login" className="inline-flex items-center gap-2 px-5 py-2.5 border border-white/30 text-white rounded-sm font-semibold text-sm hover:bg-white/10">
            Sign in
          </Link>
        </div>
        <div className="text-[10px] uppercase tracking-widest text-white/40 mt-4 opacity-0" style={{ animation: "fadeUp 0.5s 1.4s forwards" }}>
          Valid with minimum 3 users · Admin login is free
        </div>
      </div>
    </div>
  );
}

const SCENES = [
  { id: "logo", label: "Intro", icon: Sparkle, ms: 5000, render: LogoScene },
  { id: "three-modes", label: "Sign up", icon: Users, ms: 5500, render: ThreeModesScene },
  { id: "dashboard", label: "Dashboard", icon: ChartLineUp, ms: 6000, render: DashboardScene },
  { id: "checkin", label: "Check-in", icon: MapPin, ms: 6000, render: CheckInScene },
  { id: "leaves", label: "Leaves", icon: CheckCircle, ms: 6000, render: LeavesScene },
  { id: "payroll", label: "Payroll", icon: CurrencyInr, ms: 6500, render: PayrollScene },
  { id: "timesheets", label: "Timesheets", icon: Clock, ms: 6000, render: TimesheetScene },
  { id: "ninebox", label: "9-box", icon: Star, ms: 6000, render: NineBoxScene },
  { id: "reviewer", label: "360°", icon: Users, ms: 6500, render: ReviewerPickerScene },
  { id: "lms", label: "Learning", icon: GraduationCap, ms: 6500, render: LMSScene },
  { id: "ats", label: "Recruit", icon: Briefcase, ms: 6500, render: ATSScene },
  { id: "ai", label: "Ask AI", icon: ChatCircleText, ms: 5500, render: ChatAIScene },
  { id: "orgchart", label: "Matrix", icon: TreeStructure, ms: 6000, render: OrgChartScene },
  { id: "billing", label: "Billing", icon: Buildings, ms: 6500, render: BillingScene },
  { id: "mobile", label: "Mobile", icon: DeviceMobile, ms: 5500, render: MobileScene },
  { id: "outro", label: "Start free", icon: ArrowRight, ms: 5500, render: OutroScene },
];

export default function LandingTeaser() {
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(true);
  const containerRef = useRef(null);

  const totalMs = useMemo(() => SCENES.reduce((s, c) => s + c.ms, 0), []);

  // Auto-advance
  useEffect(() => {
    if (!playing) return;
    const t = setTimeout(() => setIdx((i) => (i + 1) % SCENES.length), SCENES[idx].ms);
    return () => clearTimeout(t);
  }, [idx, playing]);

  // Pause when tab is hidden
  useEffect(() => {
    const onVis = () => setPlaying(!document.hidden);
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  const Scene = SCENES[idx].render;

  return (
    <section className="relative bg-gradient-to-b from-[#f7f6f2] to-white pt-14 pb-14" data-testid="landing-product-teaser" ref={containerRef}>
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-6">
          <div className="text-[11px] uppercase tracking-[0.28em] text-zenith-teal font-bold">Watch a 90-second tour</div>
          <h3 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-2">
            The whole product, <span className="font-serif-display italic text-zenith-magenta">in one loop.</span>
          </h3>
          <div className="text-xs text-zinc-500 mt-2">16 scenes · auto-plays · click a chapter below to jump</div>
        </div>

        <div className="relative rounded-lg border border-zinc-200 bg-white shadow-2xl overflow-hidden">
          {/* Browser chrome */}
          <div className="h-8 bg-gradient-to-b from-zinc-50 to-white border-b border-zinc-200 flex items-center gap-1.5 px-3">
            <div className="w-2.5 h-2.5 rounded-full bg-red-400/60" />
            <div className="w-2.5 h-2.5 rounded-full bg-amber-400/70" />
            <div className="w-2.5 h-2.5 rounded-full bg-emerald-400/70" />
            <div className="ml-3 text-[10px] font-mono-plex text-zinc-400">zenith360.com/app/{SCENES[idx].id}</div>
            <div className="ml-auto flex items-center gap-2">
              <button
                onClick={() => setPlaying((p) => !p)}
                data-testid="teaser-playpause"
                className="w-6 h-6 grid place-items-center rounded-sm hover:bg-zinc-100 text-zinc-500"
                title={playing ? "Pause" : "Play"}
              >
                {playing ? <Pause size={12} weight="fill" /> : <Play size={12} weight="fill" />}
              </button>
              <div className="text-[10px] font-mono-plex text-zinc-400">{String(idx + 1).padStart(2, "0")} / {SCENES.length}</div>
            </div>
          </div>

          {/* Stage */}
          <div className="relative aspect-[16/8] bg-gradient-to-br from-adb-navy via-zenith-blue to-adb-navy overflow-hidden">
            <div className="absolute inset-0 orbit-grain opacity-20 pointer-events-none" />
            {/* Key on idx to restart animations */}
            <div key={idx} className="absolute inset-0" data-testid={`teaser-scene-${SCENES[idx].id}`}>
              <Scene />
            </div>
            {/* Progress bar */}
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/10">
              <div
                key={`p-${idx}-${playing ? "on" : "off"}`}
                className="h-full bg-gradient-to-r from-zenith-teal via-zenith-green to-zenith-magenta"
                style={{
                  width: 0,
                  animation: playing ? `growBar ${SCENES[idx].ms}ms linear forwards` : "none",
                  "--w": "100%",
                }}
              />
            </div>
          </div>
        </div>

        {/* Chapter ticker */}
        <div className="mt-4 grid grid-cols-4 sm:grid-cols-8 lg:grid-cols-16 gap-1">
          {SCENES.map((s, i) => {
            const Icon = s.icon;
            const active = i === idx;
            const past = i < idx;
            return (
              <button
                key={s.id}
                onClick={() => setIdx(i)}
                data-testid={`teaser-chapter-${s.id}`}
                title={s.label}
                className={`px-1.5 py-1.5 rounded-sm text-[9px] uppercase tracking-widest font-bold flex flex-col items-center gap-0.5 transition-all border ${
                  active
                    ? "bg-adb-navy text-white border-adb-navy shadow-sm"
                    : past
                    ? "bg-zenith-teal/10 text-zenith-teal-700 border-zenith-teal/30"
                    : "bg-white text-zinc-400 border-zinc-200 hover:border-adb-navy/40"
                }`}
              >
                <Icon size={12} weight={active ? "fill" : "regular"} />
                <span className="truncate max-w-full">{s.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      <style>{`
        @keyframes fadeUp { 0% { opacity: 0; transform: translateY(8px); } 100% { opacity: 1; transform: translateY(0); } }
        @keyframes slideIn { 0% { opacity: 0; transform: translateX(-8px); } 100% { opacity: 1; transform: translateX(0); } }
        @keyframes popIn { 0% { opacity: 0; transform: scale(0.6); } 60% { transform: scale(1.08); } 100% { opacity: 1; transform: scale(1); } }
        @keyframes dropPin { 0% { transform: translate(-50%, -300%); opacity: 0; } 60% { transform: translate(-50%, -95%); opacity: 1; } 80% { transform: translate(-50%, -110%); } 100% { transform: translate(-50%, -100%); opacity: 1; } }
        @keyframes pulseRing { 0% { width: 0; height: 0; opacity: 1; } 100% { width: 140px; height: 140px; opacity: 0; } }
        @keyframes growBar { 0% { width: 0; } 100% { width: var(--w, 100%); } }
        @keyframes drawLine { 0% { stroke-dashoffset: 1200; } 100% { stroke-dashoffset: 0; } }
      `}</style>
    </section>
  );
}
